import debug from 'debug';

// GQL
import { client } from '../client';
import { EXTERNAL_WORKS_BY_IDS_WARMUP } from '../queries/actions/externalWorks';
import { EXTERNAL_COMPANIES_BY_IDS_WARMUP } from '../queries/actions/externalCompanies';

const log = debug('rois:gql:selectors:warmup');

// Límite del contrato backend: `externalWorksByIds`/`externalCompaniesByIds`
// aceptan máx 200 ids por llamada. Hoy las tablas usan take ≤ 100, el troceo
// es puramente defensivo.
const BATCH_SIZE = 200;

/**
 * Warm-up de la caché normalizada de Apollo para la columna "Empresa externa"
 * de la tabla de Actuaciones.
 *
 * Resuelve los ExternalWork de todas las filas en UNA sola query
 * `externalWorksByIds` (MISMOS campos que GET_EXTERNAL_WORK_BY_ID) y, con esa
 * respuesta, las ExternalCompany asociadas en UNA sola query
 * `externalCompaniesByIds` (MISMOS campos que GET_EXTERNAL_COMPANY_BY_ID).
 * Las entidades `ExternalWork:<id>` / `ExternalCompany:<id>` quedan
 * normalizadas en caché, de modo que los `client.query(cache-first)` per-row
 * que vienen después se sirven de caché vía los redirects
 * `Query.externalWork` / `Query.externalCompany → toReference` de policies.ts,
 * en lugar de disparar 16+3 requests fila a fila.
 *
 * REGLA DE ORO — es una OPTIMIZACIÓN, jamás un gate: si falla (tablet
 * offline en un túnel, backend viejo sin las queries batch, error GraphQL,
 * lo que sea) se ignora en silencio y el selector sigue EXACTAMENTE como
 * hoy — los per-row GET_EXTERNAL_WORK_BY_ID / GET_EXTERNAL_COMPANY_BY_ID
 * intactos son el fallback natural. Este helper NUNCA lanza.
 *
 * `fetchPolicy` (default `cache-first`, el contrato original de tablas):
 * los consumidores SIN mecanismo de refresco posterior (p.ej. el resumen de
 * actuación, que no tiene polling ni flag reFetch) pasan `network-only`
 * para que cada carga baje los ExternalWork frescos en un round-trip — con
 * `cache-first`, un segundo visit con el MISMO set de ids se serviría de la
 * caché del propio warm-up (editar operatorsNumber no cambia los ids) y se
 * perdería la frescura que hoy dan los per-row network-only.
 */
export async function warmupExternalWorks(
  workIdsPerRow: (string[] | undefined | null)[],
  reFetch = false,
  fetchPolicy: 'cache-first' | 'network-only' = 'cache-first'
): Promise<void> {
  // Con reFetch=true el per-row va network-only e IGNORA la caché: el
  // warm-up solo añadiría requests extra con los mismos payloads que las
  // respuestas per-row ya traen. Solo aporta en el camino cache-first.
  if (reFetch) return;
  try {
    const uniqueWorkIds = [
      ...new Set(
        workIdsPerRow
          .flatMap((ids) => ids ?? [])
          .filter((id): id is string => Boolean(id))
      ),
    ];
    if (uniqueWorkIds.length === 0) return;

    const workBatches: string[][] = [];
    for (let i = 0; i < uniqueWorkIds.length; i += BATCH_SIZE) {
      workBatches.push(uniqueWorkIds.slice(i, i + BATCH_SIZE));
    }

    const workResults = await Promise.all(
      workBatches.map((batch) =>
        client.query({
          query: EXTERNAL_WORKS_BY_IDS_WARMUP,
          variables: { ids: batch },
          fetchPolicy,
          // 'all' cubre "backend desplegado sin externalWorksByIds" (campo
          // desconocido → error GraphQL → se ignora, no revienta).
          errorPolicy: 'all',
        })
      )
    );

    // El per-row solo resuelve la PRIMERA company de cada work
    // (externalCompanyIDs[0]) — calentamos exactamente esas.
    const uniqueCompanyIds = [
      ...new Set(
        workResults
          .flatMap((result) => result.data?.externalWorksByIds ?? [])
          .map(
            (work: { externalCompanyIDs?: string[] | null }) =>
              work?.externalCompanyIDs?.[0]
          )
          .filter((id): id is string => Boolean(id))
      ),
    ];
    if (uniqueCompanyIds.length === 0) return;

    const companyBatches: string[][] = [];
    for (let i = 0; i < uniqueCompanyIds.length; i += BATCH_SIZE) {
      companyBatches.push(uniqueCompanyIds.slice(i, i + BATCH_SIZE));
    }

    await Promise.all(
      companyBatches.map((batch) =>
        client.query({
          query: EXTERNAL_COMPANIES_BY_IDS_WARMUP,
          variables: { ids: batch },
          fetchPolicy,
          errorPolicy: 'all',
        })
      )
    );
  } catch (err) {
    // VACÍO de efectos a propósito: warm-up fallido = comportamiento de hoy.
    log('warm-up externalWorksByIds omitido (sin efectos):', err);
  }
}
